import { Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { GridsterConfig, GridsterItem, GridsterItemComponent, GridsterItemComponentInterface } from 'angular-gridster2';
import { COL_GAP, COL_WIDTH, DOCKER_WIDTH, gridsterConfig } from './gridster.config';
import { AddWidgetComponent } from './widget/add-widget-widget/add-widget-widget.component';
import { Widget, WidgetIdentity, WidgetPosition } from '../models/dashboard.models';
import { DashboardService } from '../services/dashboard.service';
import { CMSLanguageService } from '@markant-platform/shared/data-access';
import { CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DashboardConfig } from './configuration/dashboard-widgets.config';
import { WidgetStore } from '@markant-platform/widgets/data-access';
import { Router } from '@angular/router';

@Component({
  selector: 'markant-platform-dashboard',
  templateUrl: './dashboard-widgets.component.html',
  styleUrls: ['./dashboard-widgets.component.scss'],
})
export class DashboardWidgetsComponent implements OnInit {
  protected readonly store = inject(WidgetStore);
  private readonly router: Router = inject(Router);
  get visibleWidgets(): Widget[] {
    return this._visibleWidgets;
  }

  set visibleWidgets(visibleWidgets: Widget[]) {
    this._visibleWidgets = visibleWidgets;

    const rearrangeableWidgets = this._visibleWidgets.filter(
      widget => !DashboardConfig.UNDRAGGABLE_WIDGET_IDS.includes(widget.id)
    );

    this.shouldDisplayArrangeButton = rearrangeableWidgets.length > 1;

    if (this._visibleWidgets) {
      this.savedDashboard = this.mapWidgetsToDashboard(this._visibleWidgets);
      this.dashboard = this.mapWidgetsToDashboard(this._visibleWidgets);
    }
  }

  private _visibleWidgets: Widget[] = [];
  shouldDisplayArrangeButton = false;

  loading = true;

  options: GridsterConfig = {
    ...gridsterConfig,
    minCols: this.getColumnCountForCurrentWindowWidth(), // minimum columns in grid defined by the design
    maxCols: this.getColumnCountForCurrentWindowWidth(), // maximum columns in grid defined by the design
    draggable: {
      ...gridsterConfig.draggable,
      start: (_, event) => this.addClassToChild(event),
      stop: (_, event) => this.removeClassFromChild(event),
    },
  };

  private savedDashboard: (GridsterItem & Widget)[] = [];
  dashboard: (GridsterItem & Widget)[] = [];

  roles: string[] = [];
  userLabel = '';
  hasAvailableWidgets = false;
  hasAddableWidgets = false;

  addWidgetId: string = AddWidgetComponent.gridsterDef['id'];

  constructor(
    private readonly dashboardService: DashboardService,
    private readonly cmsLanguageService: CMSLanguageService,
    private readonly cartridgeFacade: CartridgesFacade,
    private readonly destroyRef: DestroyRef
  ) {
    dashboardService.widgetRemovalTrigger$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(widget => this.removeWidget(widget));
  }

  async ngOnInit(): Promise<void> {
    this.userLabel = await firstValueFrom(this.dashboardService.getUserName$());
    this.roles = await firstValueFrom(this.dashboardService.getUserRoles$());
    const qualificationLevel = await firstValueFrom(this.dashboardService.getOrganizationQualificationLevel$());
    const { hasAvailableWidgets, hasAddableWidgets } = await firstValueFrom(
      this.dashboardService.hasAvailableAndAddableWidgets$(this.roles, qualificationLevel)
    );

    this.hasAvailableWidgets = hasAvailableWidgets;
    this.hasAddableWidgets = hasAddableWidgets;

    if (this.hasAvailableWidgets) {
      this.cmsLanguageService.activeCMSLanguage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
        this.visibleWidgets = await firstValueFrom(this.dashboardService.getUpdatedVisibleWidgets(this.roles));
      });
    }

    this.loading = false;
  }

  /**
   * Callback function that adds focus class from gridster element child
   * @param gridsterItem - gridster element that got focus
   */
  setFocusToChild(gridsterItem: GridsterItemComponent): void {
    (gridsterItem.el.querySelector('*') as Element).classList.add('focused');
  }

  /**
   * Callback function that removes focus class from gridster element child
   * @param gridsterItem - gridster element that lost focus
   */
  removeFocusFromChild(gridsterItem: GridsterItemComponent): void {
    (gridsterItem.el.querySelector('*') as Element).classList.remove('focused');
  }

  /**
   * Enables drag mode and hides AddWidget element.
   */
  enableDragDrop(): void {
    if (this.options.draggable) {
      this.options.draggable.enabled = true;

      this.dashboard = this.removeAddWidgetElement(this.dashboard);

      this.gridsterOptionsChanged();
    }
  }

  /**
   * Disables drag mode, persists the current widget position and shows the AddWidget element.
   */
  async disableDragDropAndSave(widgets: Widget[] | undefined): Promise<void> {
    if (this.options.draggable) {
      this.options.draggable.enabled = false;
      this.gridsterOptionsChanged();
      await this.persistWidgetPosition(widgets);

      this.dashboard = this.addAddWidgetElement(this.dashboard);
    }
  }

  async removeWidget(widget: WidgetIdentity): Promise<void> {
    const positionsWithoutRemovedWidget = this.dashboard
      .filter(
        ({ id, cartridgeId }) => !(widget.id === id && (widget.cartridgeId == cartridgeId || widget.id === cartridgeId))
      )
      .map(widget => ({
        widgetId: widget.id,
        gridPositionX: widget.x,
        gridPositionY: widget.y,
        widgetType: widget.widgetType,
        cartridgeId: widget.cartridgeId,
      }));

    this.visibleWidgets = await firstValueFrom(
      this.dashboardService.updateVisibleWidgets(positionsWithoutRemovedWidget)
    );
  }

  /**
   * On Window resize the columns will be recalculated.
   * When a change in the number of columns occur, the widgets will be readded so that their position will
   * be correctly set for the current size
   */
  @HostListener('window:resize')
  updateColumns(): void {
    const fittedColumns = this.getColumnCountForCurrentWindowWidth();

    const lastColumnWithWidget = this.savedDashboard.reduce((acc, cur) => Math.max(acc, cur.x + cur.cols), 0);

    if (fittedColumns != this.options.maxCols && fittedColumns >= 2) {
      this.options.minCols = fittedColumns;
      this.options.maxCols = fittedColumns;

      // Only update dashboard if widget position does not fit new column count
      if (lastColumnWithWidget >= fittedColumns) {
        this.dashboard = [];
        this.savedDashboard.forEach(dashboardElement => this.dashboard.push({ ...dashboardElement }));

        // recalculate position for 'add widget' widget
        const lastPosition = this.calculateLastPositionInWidgetArray(this.dashboard);
        const addWidgetItem = this.dashboard.find(element => element.id === this.addWidgetId);
        if (addWidgetItem) {
          addWidgetItem.x = lastPosition[0];
          addWidgetItem.y = lastPosition[1];
        }
      }

      this.gridsterOptionsChanged();
    }
  }

  /**
   * It will  send an updated WidgetPosition Array to the output when at least one position changed.
   */
  private async persistWidgetPosition(widgets: Widget[] | undefined): Promise<void> {
    const itemIdToItem: { [itemId: string]: GridsterItem & Widget } = this.dashboard.reduce(
      (itemLookupMap: { [itemId: string]: GridsterItem & Widget }, widgetItem: GridsterItem & Widget) => {
        itemLookupMap[widgetItem.cartridgeId ?? widgetItem.id] = widgetItem;

        return itemLookupMap;
      },
      {}
    );

    if (widgets) {
      let positionChanged = false;
      const updatedPositions: WidgetPosition[] = widgets.map(widget => {
        const dashboardItem = itemIdToItem[widget.cartridgeId ?? widget.id];

        if (dashboardItem && (widget.gridPositionX !== dashboardItem.x || widget.gridPositionY !== dashboardItem.y)) {
          positionChanged = true;
        }

        return {
          widgetId: widget.id,
          gridPositionX: dashboardItem.x,
          gridPositionY: dashboardItem.y,
          widgetType: widget.widgetType,
          cartridgeId: widget.cartridgeId,
        };
      });

      if (positionChanged) {
        this.visibleWidgets = await firstValueFrom(this.dashboardService.updateVisibleWidgets(updatedPositions));
      }
    }
  }

  private addClassToChild(gridsterItem: GridsterItemComponentInterface): void {
    (gridsterItem.el.querySelector('*') as Element).classList.add('move-active');
  }

  private removeClassFromChild(gridsterItem: GridsterItemComponentInterface): void {
    (gridsterItem.el.querySelector('*') as Element).classList.remove('move-active');
  }

  private gridsterOptionsChanged(): void {
    // trigger gridster-internal optionsChanged function to redraw the columns correctly otherwise gridster wouldn't recognize the option change
    // we have to check for existence because api and optionsChanged are defined optional
    if (this.options.api && this.options.api.optionsChanged) {
      this.options.api.optionsChanged();
    }
  }

  private getColumnCountForCurrentWindowWidth(): number {
    //window.innerWidth - DOCKER_WIDTH  - (COL_GAP * 2) = current window size minus the right margin calculated by Gridster and minus the docker width
    // COL_WIDTH + COL_GAP = width of a single column
    return Math.floor((window.innerWidth - DOCKER_WIDTH - COL_GAP * 2) / (COL_WIDTH + COL_GAP));
  }

  private mapWidgetsToDashboard(widgets: Widget[]): (GridsterItem & Widget)[] {
    const updatedDashboard: (GridsterItem & Widget)[] = widgets.map(widget => ({
      ...widget,
      cols: Number(widget.size.split('x')[1]),
      rows: Number(widget.size.split('x')[0]),
      x: widget.gridPositionX || 0,
      y: widget.gridPositionY || 0,
    }));

    return this.addAddWidgetElement(updatedDashboard);
  }

  private addAddWidgetElement(dashboard: (GridsterItem & Widget)[]): (GridsterItem & Widget)[] {
    if (dashboard.some(element => element.id === AddWidgetComponent.gridsterDef['id'])) {
      return dashboard;
    }

    const lastPosition = this.calculateLastPositionInWidgetArray(dashboard);

    return [
      ...dashboard,
      {
        ...AddWidgetComponent.gridsterDef,
        x: lastPosition[0],
        y: lastPosition[1],
      } as GridsterItem & Widget,
    ];
  }

  private removeAddWidgetElement(dashboard: (GridsterItem & Widget)[]): (GridsterItem & Widget)[] {
    return dashboard.filter(element => element.id !== AddWidgetComponent.gridsterDef['id']);
  }
  async navigateToFurtherQualification(): Promise<void> {
    await this.router.navigateByUrl('/enable-organization');
  }
  closerFurtherQualification(): void {
    this.store.toggleCompleteProfileDialogWidget(false);
  }
  private calculateLastPositionInWidgetArray(mappedWidgets: (GridsterItem & Widget)[]): [number, number] {
    const lastPosition: [number, number] = mappedWidgets.reduce(
      (possibleLastPosition, widget) => {
        possibleLastPosition[0] = Math.max(possibleLastPosition[0], widget.x + widget.cols);
        possibleLastPosition[1] = Math.max(possibleLastPosition[1], widget.y + widget.rows - 1);
        return possibleLastPosition;
      },
      [0, 0]
    );

    // Last position moves one row down when current row has no free columns anymore
    if (lastPosition[0] >= this.getColumnCountForCurrentWindowWidth()) {
      lastPosition[0] = 1;
      lastPosition[1] += 1;
    }

    return lastPosition;
  }
}
